import Loader from "@components/Loader";
import AdvancementAccordion from "@components/dashboard/advancements/AdvancementAccordion";
import AiActionComposer from "@components/dashboard/ai/actions/AiActionComposer";
import { useProjectActions, useProjectContext } from "@contexts/projects";
import { formatNumberToCurrency } from "@utils/formatNumber";
import { useEffect, useRef, useState } from "react";
import { isPresentationProject } from "@utils/presentationMode";
import { isDisbursementPlanningReadOnly } from "@components/dashboard/financialProjection/cashFlowPlanning";


const renderRow = (label, value) => {
  return <div className="data">
    <span className="accordion-label">{label}</span>
    <span className="value">{value}</span>
  </div>
}

const CreatedItem = ({ resource, handleDelete, readOnly }) => {
  const [open, setOpen] = useState(true)

  return <div className={`accordion-item offcanvas-accordion-item curve margin-bottom-8`}>
    <div>
      <div className="flex align-center margin-bottom-8">
        <button
          className={`accordion-toggle ${open ? "active" : ""}`}
          onClick={() => setOpen(!open)}
        />
        <span className="accordion-title" title={resource.name} >{resource.name}</span>
      </div>
    </div>
    {open && resource.data.map(i => {
      return <div key={i.entryId || `${i.from}-${i.to}-${i.value}-${i.updated_at || ''}`} className="grid gap-16 columns-5-laptop-l margin-top-12">
        {renderRow(`Retirado de`, i.from)}
        {renderRow(`Adicionado em`, i.to)}
        {renderRow('Criado em', new Date(i.updated_at).toLocaleDateString('pt-BR'))}
        {renderRow('Valor', formatNumberToCurrency(i.value))}
        {!readOnly && <button style={{ justifySelf: 'end' }} className="button button-error small" onClick={() => handleDelete(i, resource)}>Deletar</button>}
      </div>
    })}
  </div>
}


const Advancements = () => {
  const { getAppropriation, getAdvancements, updateAdvancement, getAllBuildingItems } = useProjectActions();
  const [resources, setResources] = useState([])
  const [loading, setLoading] = useState(false)
  const [allAdvancements, setAllAdvancements] = useState([])
  const [search, setSearch] = useState('')
  const [createdVisible, setCreatedVisible] = useState(true)
  const [openItems, setOpenItems] = useState({});
  const [levelToGet, setLevelToGet] = useState('')
  const [levelItems, setLevelItems] = useState([])
  const [itemToFilter, setItemToFilter] = useState('')

  const toggleItem = (wbs_code_id) => {
    setOpenItems((prev) => ({
      ...prev,
      [wbs_code_id]: !prev[wbs_code_id],
    }));
    const element = document.getElementById(wbs_code_id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const { activeProject } = useProjectContext()
  const readOnly = isPresentationProject(activeProject)
  const usesPhysicalFinancial = isDisbursementPlanningReadOnly(
    activeProject?.cashFlowPlanning,
  )
  const planningReadOnly = readOnly || usesPhysicalFinancial
  const activeWorkIdRef = useRef(activeProject?._id)
  const requestGenerationRef = useRef(0)
  activeWorkIdRef.current = activeProject?._id

  const getItems = async (project) => {
    const generation = ++requestGenerationRef.current
    const workId = project?._id
    const buildingId = project?.building_id
    const isCurrentRequest = () => (
      activeWorkIdRef.current === workId
      && requestGenerationRef.current === generation
    )
    if (!workId || !buildingId) {
      if (isCurrentRequest()) {
        setResources([])
        setAllAdvancements([])
        setLoading(false)
      }
      return
    }
    setLoading(true)
    try {
      const response = await getAppropriation(project.appropriation_level)
      if (!isCurrentRequest()) return
      setResources([...(response?.uniqueDescs || [])].sort((a, b) => a.wbs_description.localeCompare(b.wbs_description)))
      const all = await getAdvancements(buildingId)
      if (!isCurrentRequest()) return
      setAllAdvancements(Array.isArray(all?.response) ? all.response : [])
    } finally {
      if (isCurrentRequest()) setLoading(false)
    }
  }

  useEffect(() => {
    getItems(activeProject)
  }, [activeProject])

  const refreshAdvancements = async () => {
    const generation = ++requestGenerationRef.current
    const workId = activeProject?._id
    const buildingId = activeProject?.building_id
    if (!workId || !buildingId) return
    const isCurrentRefresh = () => (
      activeWorkIdRef.current === workId
      && requestGenerationRef.current === generation
    )
    try {
      const refreshed = await getAdvancements(buildingId)
      if (!isCurrentRefresh()) return
      setAllAdvancements(Array.isArray(refreshed?.response) ? refreshed.response : [])
    } catch {
      // Estas outlets não exibem erro de carga; preserva os dados atuais.
    } finally {
      if (isCurrentRefresh()) setLoading(false)
    }
  }

  const getLevelItems = async () => {
    const res = await getAllBuildingItems([levelToGet])
    setLevelItems(res.items)
  }

  useEffect(() => {
    if (levelToGet !== '') {
      getLevelItems()

    }
  }, [levelToGet])

  const handleDelete = async (item, resource) => {
    const response = await updateAdvancement({
      operation: 'remove',
      wbs: resource.wbs,
      ...(item.entryId ? { entryId: item.entryId } : {}),
      from: item.from,
      to: item.to,
    })
    if (response?.code === 200) {
      const refreshed = await getAdvancements(activeProject.building_id)
      setAllAdvancements(Array.isArray(refreshed?.response) ? refreshed.response : [])
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);

    const filteredResources = resources.filter((i) =>
      i.description.toLowerCase().includes(value.toLowerCase())
    );
    const newOpenItems = {};
    filteredResources.forEach((item) => {
      newOpenItems[item.wbs_code_id] = openItems[item.wbs_code_id] || false;
    });
    setOpenItems(newOpenItems);
  };


  return (
    <>
      {loading && <div className="margin-top-24"><Loader /></div>}
      <div className="resources-container">
        {usesPhysicalFinancial ? (
          <div className="alert alert-info margin-bottom-24" role="status">
            <h3>Antecipações fora da previsão atual</h3>
            <p>A organização usa o previsto físico-financeiro. Antecipações do desembolso permanecem visíveis para consulta, mas não alteram a previsão de caixa.</p>
          </div>
        ) : null}
        {!readOnly && <>
        <p style={{ fontSize: '24px', fontWeight: 'bold', color: 'grey' }}>Todos itens</p>
        <div className="flex justify-content-center align-center gap-8">
          <span>Filtrar por:</span>
          <select style={{ marginBottom: '0px', marginTop: '0px' }} onChange={(e) => {
            setLevelToGet(e.target.value)
            setItemToFilter('')
          }} value={levelToGet}>
            <option value='' disabled>Selecione</option>
            <option value='units'>Unidade Construtiva</option>
            <option value='cells'>Célula</option>
            <option value='steps'>Etapa</option>
            <option value='substeps'>Subetapa</option>
            <option value='services'>Serviço</option>
          </select>
          {levelItems.length > 0 && <select style={{ marginBottom: '0px', marginTop: '0px' }} onChange={(e) => setItemToFilter(e.target.value)} value={itemToFilter}>
            <option value='' disabled>Selecione</option>
            {levelItems.map(i => {
              return <option key={i.wbs_code_id} value={i.wbs_code_id}>{i.building_unit_description || i.wbs_description}</option>
            })}
          </select>}
          {itemToFilter !== '' && <button style={{ alignSelf: 'center' }} className="button button-secondary" onClick={() => setItemToFilter('')}>Limpar</button>}
        </div>
        <input type="text" value={search} onChange={handleSearch} placeholder="Buscar por item" />
        <div className="accordion">
          {resources?.length > 0 && (
            <>
              <div className="accordion">
                {resources.filter(i => i.wbs_code_id.includes(itemToFilter || '')).filter(i => i.wbs_description.toLowerCase().includes(search.toLowerCase())).map(i => {
                  return <AdvancementAccordion
                    key={i.wbs_code_id}
                    item={i}
                    wbs={i.wbs_code_id}
                    isOpen={!!openItems[i.wbs_code_id]}
                    toggleItem={toggleItem}
                  />
                })}
              </div>
            </>
          )}
        </div>
        </>}
        {!planningReadOnly && activeProject?._id && <section className="margin-bottom-24" aria-label="Criar com IA">
          <h2>Criar com IA</h2>
          <AiActionComposer
            workId={activeProject._id}
            module="advancement"
            onExecuted={refreshAdvancements}
          />
        </section>}
        {allAdvancements?.length > 0 && <>
          <div className="flex align-center margin-bottom-8">
            <button
              className={`accordion-toggle ${createdVisible ? "active" : ""}`}
              onClick={() => setCreatedVisible(!createdVisible)}
            />
            <span style={{ fontSize: '24px', fontWeight: 'bold', color: 'grey' }}>Antecipações já criadas</span>
          </div>
          {createdVisible && allAdvancements?.filter(i => i.data?.length > 0).map((resource) => {
            return <CreatedItem key={resource._id || `${resource.building_id}-${resource.wbs}`} resource={resource} handleDelete={handleDelete} readOnly={planningReadOnly} />
          })}
        </>}
      </div>
    </>
  );
};

export default Advancements;
